import React, { useMemo, type FC } from "react";
import { useSearchParams } from "react-router-dom";
import { useGetCampaignClone } from "../../../../queries/dynamicBonuses/useGetCampaignClone";
import { useGetTemplate } from "../../../../queries/dynamicBonuses/useGetTemplate";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { formatString } from "../../../../utils/constants/stringUtils";
import CreateCampaignWizard from "./CreateCampaignWizard/CreateCampaignWizard";
import {
  DEFAULT_CAMPAIGN_TYPE,
  campaignCloneToWizardValues,
  parseWizardCampaignType,
  templateToWizardValues,
  wizardInitialValuesForCampaignType,
  type WizardFormValues,
} from "./CreateCampaignWizard/toWizardCampaignIn";

const CreateCampaignPage: FC = () => {
  const [searchParams] = useSearchParams();
  const cloneFrom = searchParams.get("cloneFrom")?.trim() ?? "";
  const templateId = cloneFrom ? "" : searchParams.get("templateId")?.trim() ?? "";
  const campaignType = parseWizardCampaignType(searchParams.get("campaignType"));
  const {
    data: clone,
    isLoading: cloneLoading,
    isError: cloneError,
  } = useGetCampaignClone(cloneFrom);
  const {
    data: template,
    isLoading,
    isError,
  } = useGetTemplate(templateId);

  const initialValues = useMemo<Partial<WizardFormValues> | undefined>(() => {
    if (clone) {
      return campaignCloneToWizardValues(clone);
    }

    if (template) {
      const type = campaignType ?? DEFAULT_CAMPAIGN_TYPE;
      const templateName = template.name.trim();
      return {
        ...wizardInitialValuesForCampaignType(type),
        ...templateToWizardValues(template),
        campaign_type: type,
        start_mode: "template",
        name: templateName
          ? formatString(AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_NAME_FROM_TEMPLATE, {
              templateName,
            })
          : undefined,
      };
    }

    if (campaignType) {
      return wizardInitialValuesForCampaignType(campaignType);
    }

    return undefined;
  }, [campaignType, clone, template]);

  const isPrefillLoading =
    (Boolean(cloneFrom) && cloneLoading) || (Boolean(templateId) && isLoading);
  const initialValuesError = cloneFrom && cloneError
    ? AppStrings.PAM_DYNAMIC_BONUS_CLONE_CAMPAIGN_LOAD_ERROR
    : templateId && isError
      ? "Failed to load the selected template."
      : undefined;

  return (
    <CreateCampaignWizard
      initialValues={initialValues}
      isInitialValuesLoading={isPrefillLoading}
      initialValuesError={initialValuesError}
    />
  );
};

export default CreateCampaignPage;
